// Allocation History: an analyst book's broad-asset-class weights at each saved change
// (and today), or the same relative to DS70 as it was on each date — the trading app's
// approved set then in force, or DS70's Shadow_Data targets before trading's first
// approved DS70 set. Holdings hold until the next change, so the lines are stepped and
// the x-axis is real time.
import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, TimeScale, Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import { API, fmtDate, getAssetClassColor, useApiGet } from './apUtils';
import { BookSelect, Notice, Toggle } from './apComponents';

ChartJS.register(LinearScale, TimeScale, PointElement, LineElement, Tooltip, Legend, Filler);

export default function AllocationHistoryTab({ code, setCode, books }) {
  const [view, setView] = useState('weights');
  const { data, error, loading } = useApiGet(code ? `${API}/portfolios/${code}/allocation-history` : null, { view });
  const relative = view === 'relative';

  const chart = useMemo(() => {
    if (!data?.dates?.length) return null;
    return {
      datasets: Object.entries(data.series).map(([ac, values], i) => ({
        label: ac,
        data: data.dates.map((d, k) => ({ x: d, y: values[k] })),
        borderColor: getAssetClassColor(ac, i),
        backgroundColor: getAssetClassColor(ac, i) + (relative ? '00' : '20'),
        fill: !relative,
        stepped: 'after',
        spanGaps: false,
        pointRadius: 3,
        borderWidth: 2,
      })),
    };
  }, [data, relative]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, padding: 15, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          title: (items) => fmtDate(items[0]?.raw?.x),
          label: (ctx) => (ctx.parsed.y == null ? `${ctx.dataset.label}: no DS70 reference`
            : `${ctx.dataset.label}: ${relative && ctx.parsed.y > 0 ? '+' : ''}${ctx.parsed.y.toFixed(1)}%`),
        },
      },
    },
    scales: {
      x: { type: 'time', time: { unit: 'month', tooltipFormat: 'dd MMM yy' }, grid: { display: false },
        ticks: { font: { size: 10 } } },
      y: relative
        ? { grid: { color: (c) => (c.tick?.value === 0 ? '#012341' : '#f0f0f0') },
          ticks: { callback: (v) => `${v > 0 ? '+' : ''}${v}%`, font: { size: 10 } },
          title: { display: true, text: 'Relative to DS70 at the time (pp)', font: { size: 11 } } }
        : { stacked: true, min: 0, max: 100, grid: { color: '#f0f0f0' },
          ticks: { callback: (v) => `${v}%`, font: { size: 10 } },
          title: { display: true, text: 'Weight (%)', font: { size: 11 } } },
    },
  }), [relative]);

  return (
    <div className="tab-panel">
      <BookSelect id="ap-history-book" value={code} onChange={setCode} books={books}>
        <Toggle options={[['weights', 'Weights'], ['relative', 'Relative to DS70']]} value={view} onChange={setView} />
      </BookSelect>
      {error && <Notice kind="error">Failed to load: {error}</Notice>}
      {loading && <div className="chart-loading">Loading allocation history...</div>}
      {!loading && data && !data.dates.length && <div className="no-data-message">No holdings saved for this book.</div>}
      {chart && (
        <>
          {data.issues?.length > 0 && (
            <Notice kind="warn">
              Not on the AP List exactly once (saved class used): {data.issues.map((i) => i.symbol).join(', ')}
            </Notice>
          )}
          <div className="chart-container"><Line data={chart} options={options} /></div>
          <table className="ap-points-table">
            <thead>
              <tr><th>Date</th><th>Holdings</th>{relative && <th>DS70 compared with</th>}</tr>
            </thead>
            <tbody>
              {data.points.map((p) => (
                <tr key={p.date}>
                  <td>{fmtDate(p.date)}{p.is_today ? ' (today)' : ''}</td>
                  <td>{p.is_today ? `Unchanged since ${fmtDate(p.set_effective)}` : `Set saved from ${fmtDate(p.set_effective)}`}</td>
                  {relative && (
                    <td>
                      {p.ds70 ? p.ds70.label : 'No DS70 reference for this date'}
                      {p.ds70 && Math.abs(p.ds70.total - 100) > 0.01 ? ` (totals ${p.ds70.total.toFixed(2)}%)` : ''}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
